import React, { useState, useEffect } from 'react';
import { Eye, EyeOff, Lock, CheckCircle, XCircle } from 'lucide-react';
import { 
  validatePasswordStrength, 
  getPasswordStrengthLevel, 
  getStrengthMeterColor,
  getStrengthMeterWidth,
  PASSWORD_MIN_LENGTH 
} from '../utils/passwordValidator';

interface PasswordInputProps {
  id: string;
  name: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
  autoComplete?: string;
  required?: boolean;
  showStrengthIndicator?: boolean;
  label?: string;
  showLabel?: boolean;
}

const PasswordInput: React.FC<PasswordInputProps> = ({
  id,
  name,
  value,
  onChange,
  placeholder = 'Password',
  autoComplete = 'new-password',
  required = false,
  showStrengthIndicator = false,
  label,
  showLabel = false
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const [validationResult, setValidationResult] = useState<ReturnType<typeof validatePasswordStrength> | null>(null);
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (value && showStrengthIndicator) {
      setValidationResult(validatePasswordStrength(value));
    } else {
      setValidationResult(null);
    }
  }, [value, showStrengthIndicator]);

  const handleBlur = () => {
    if (value) {
      setTouched(true);
    }
  };

  const strengthLevel = validationResult ? getPasswordStrengthLevel(validationResult.score) : null;
  const showFeedback = showStrengthIndicator && value && touched;

  return (
    <div>
      {showLabel && label && (
        <label htmlFor={id} className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          {label} {required && '*'}
        </label>
      )}
      {!showLabel && label && (
        <label htmlFor={id} className="sr-only">
          {label}
        </label>
      )}
      
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <Lock className="h-5 w-5 text-gray-400 dark:text-gray-500" />
        </div>
        <input
          id={id}
          name={name}
          type={showPassword ? 'text' : 'password'}
          autoComplete={autoComplete}
          required={required}
          className="appearance-none relative block w-full px-3 py-2 pl-10 pr-10 border border-gray-300 dark:border-gray-600 placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-white bg-white dark:bg-gray-700 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:focus:ring-primary-400 dark:focus:border-primary-400 focus:z-10 sm:text-sm"
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          onBlur={handleBlur}
        />
        <button
          type="button"
          className="absolute inset-y-0 right-0 pr-3 flex items-center"
          onClick={() => setShowPassword(!showPassword)}
        >
          {showPassword ? (
            <EyeOff className="h-5 w-5 text-gray-400 dark:text-gray-500" />
          ) : (
            <Eye className="h-5 w-5 text-gray-400 dark:text-gray-500" />
          )}
        </button>
      </div>

      {/* Password strength indicator */}
      {showStrengthIndicator && value && (
        <div className="mt-2">
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs text-gray-600 dark:text-gray-400 dark:text-gray-500">Password strength:</span>
            {strengthLevel && (
              <span className={`text-xs font-medium ${strengthLevel.color}`}>
                {strengthLevel.label}
              </span>
            )}
          </div>
          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
            <div
              className={`h-2 rounded-full transition-all duration-300 ${getStrengthMeterColor(validationResult?.score)}`}
              style={{ width: `${getStrengthMeterWidth(validationResult?.score)}%` }}
            />
          </div>
        </div>
      )}

      {/* Password requirements */}
      {showStrengthIndicator && (
        <div className="mt-3 space-y-2">
          <p className="text-xs font-medium text-gray-700 dark:text-gray-300">Password requirements:</p>
          <div className="space-y-1">
            <div className={`flex items-center text-xs ${value.length >= PASSWORD_MIN_LENGTH ? 'text-green-600' : 'text-gray-500 dark:text-gray-400 dark:text-gray-500'}`}>
              {value.length >= PASSWORD_MIN_LENGTH ? (
                <CheckCircle className="h-3.5 w-3.5 mr-1.5" />
              ) : (
                <XCircle className="h-3.5 w-3.5 mr-1.5" />
              )}
              At least {PASSWORD_MIN_LENGTH} characters
            </div>
            <div className={`flex items-center text-xs ${validationResult?.valid ? 'text-green-600' : 'text-gray-500 dark:text-gray-400 dark:text-gray-500'}`}>
              {validationResult?.valid ? (
                <CheckCircle className="h-3.5 w-3.5 mr-1.5" />
              ) : (
                <XCircle className="h-3.5 w-3.5 mr-1.5" />
              )}
              Strong password (not easily guessable)
            </div>
          </div>
        </div>
      )}

      {/* Validation feedback */}
      {showFeedback && validationResult && !validationResult.valid && (
        <div className="mt-2 text-xs text-red-600">
          {validationResult.feedback?.warning && (
            <p className="font-medium">{validationResult.feedback.warning}</p>
          )}
          {validationResult.feedback?.suggestions && validationResult.feedback.suggestions.length > 0 && (
            <ul className="mt-1 list-disc list-inside space-y-0.5">
              {validationResult.feedback.suggestions.map((suggestion, idx) => (
                <li key={idx}>{suggestion}</li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default PasswordInput;
